import React, { Component } from 'react';
import { t } from '../i18n';
import ApiService from '../services/api';
import nativeBridge, { openLink } from '../widget/native-bridge.js';
import { parseColor } from '../widget/theme-color';

// 通知渠道卡片（service_data.type = request_binding / offline_binding）。
//
// 2026-08 合并重做。此前这件事被拆成两张卡、两条消息：
//   卡 1（本文件旧版）让访客选渠道 → 点一下往对话流里再塞卡 2（BindingMessage /
//   EmailBinding）去做授权。两张卡风格不一致（卡 2 用 theme.mainColor 画 2px 边框
//   + 实心按钮，商户主色是红色时整张卡血红，像报错），流程割裂，而且
//   「已经绑过的渠道」点下去毫无反馈。
//
// 现在：一张卡走完「看状态 → 选渠道 → 授权 → 出结果」，状态在卡内原地变化，
// 不再往对话流追加消息。
//
// 三条硬规矩：
//   1. 每个渠道的初始状态来自真实数据 —— 服务端 bindingStatus（clients.email /
//      telegram_user_id / push_subscriptions）+ 浏览器侧 Notification.permission
//      与 getSubscription() 复核，不用前端本地状态猜。
//   2. 任何一次点击都必须有可见反馈：要么进 working 态，要么落到终态
//      （on / blocked / error），要么展开内联面板。没有 no-op 分支。
//   3. 文案全部走 t()，视觉沿用富消息卡片（.lc-card）那套：白底 + #e5e7eb 描边 +
//      12px 圆角，主色只用于小面积图标托盘与强调，不画渐变、不用渠道品牌色。

const NEUTRAL_BORDER = 'var(--lc-border, #e5e7eb)';
// HAIRLINE 历史上一个常量身兼三职（分隔线 border / 中性 chip 底 / tintOf 兜底）。
// 暗色下这三职对应两个不同 token：border 用 --lc-border，填充用 --lc-surface-2。
// 拆成两个常量，亮色 fallback 都是原字面量 #f3f4f6，像素不变。
const HAIRLINE = 'var(--lc-surface-2, #f3f4f6)';           // 填充类（chip 底 / tintOf 兜底）
const HAIRLINE_BORDER = 'var(--lc-border, #f3f4f6)';       // 分隔线类
const TITLE_COLOR = 'var(--lc-text, #111827)';
const BODY_COLOR = 'var(--lc-text-2, #4b5563)';
const MUTED_COLOR = 'var(--lc-text-2, #6b7280)';
const FAINT_COLOR = 'var(--lc-text-3, #9ca3af)';
const ROW_HOVER_BG = 'var(--lc-bg, #f9fafb)';
// 颜色专项 / 裁决 R13：这三个原本是写死的 green-700 / green-50 / amber-700。
// 两个毛病：① `#f0fdf4` 是**浅色**底，暗色模式下这枚状态芯片是一块亮斑
// （探针实测 background rgb(240,253,244) 在暗底上未变）；② 绿和琥珀各写各的，
// 与 R13 收敛出来的 semantic.status 三色不是同一批值，三端对不上。
//
// 改法：色相走 --lc-success / --lc-warning（亮暗恒定，本来就是为这类"系统对
// 操作的判断"准备的），底色走同色系 alpha 兑底 —— 兑的是当前 surface，所以
// 亮色下是浅绿、暗色下是深绿，不再是一块写死的亮斑。
const OK_COLOR = 'var(--lc-success)';
const OK_TINT = 'color-mix(in srgb, var(--lc-success) 12%, var(--lc-surface, #ffffff))';
const WARN_COLOR = 'var(--lc-warning)';
const WARN_TINT = 'color-mix(in srgb, var(--lc-warning) 12%, var(--lc-surface, #ffffff))';

/**
 * 主题色 → 同色系浅底（图标托盘）。conf.mainColor 可以是任意 CSS 颜色，
 * 只有 parseColor 认得（#rgb/#rgba/#rrggbb/#rrggbbaa/rgb()/rgba()）时才做 alpha
 * 混合，否则回落中性灰底 —— 不能因为商户填了 'red' 或渐变就把托盘画成非法色值。
 */
function tintOf(color) {
    const c = parseColor(color);
    if (!c) return HAIRLINE;
    // 主色自己的 alpha 有意不乘进来：托盘要的是"品牌色相的 9% 淡底"这个固定观感，
    // 商户把主色调成半透明不该让托盘再淡一层、直接看不见。
    return `rgba(${c.r}, ${c.g}, ${c.b}, 0.09)`;
}

const iconBase = {
    width: 16, height: 16, display: 'block', flexShrink: 0,
    fill: 'none', stroke: 'currentColor', strokeWidth: 1.8,
    strokeLinecap: 'round', strokeLinejoin: 'round',
};

const BellIcon = () => (
    <svg viewBox="0 0 24 24" style={iconBase} aria-hidden="true">
        <path d="M18 8.5a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14.5 18 8.5" />
        <path d="M13.7 19.5a2 2 0 0 1-3.4 0" />
    </svg>
);
const ClockIcon = () => (
    <svg viewBox="0 0 24 24" style={iconBase} aria-hidden="true">
        <circle cx="12" cy="12" r="9" /><path d="M12 7v5.2l3.2 2" />
    </svg>
);
const TelegramIcon = () => (
    <svg viewBox="0 0 24 24" style={{ ...iconBase, fill: 'currentColor', stroke: 'none' }} aria-hidden="true">
        <path d="M9.78,18.65L10.06,14.42L17.74,7.5C18.08,7.19 17.67,7.04 17.22,7.31L7.74,13.3L3.64,12C2.76,11.75 2.75,11.14 3.84,10.7L19.81,4.54C20.54,4.21 21.24,4.72 20.96,5.84L18.24,18.65C18.05,19.56 17.5,19.78 16.74,19.36L12.6,16.3L10.61,18.23C10.38,18.46 10.19,18.65 9.78,18.65Z" />
    </svg>
);
const MailIcon = () => (
    <svg viewBox="0 0 24 24" style={iconBase} aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3.5 6.5 12 12.5l8.5-6" />
    </svg>
);
const ChevronIcon = () => (
    <svg viewBox="0 0 24 24" style={{ ...iconBase, width: 14, height: 14, strokeWidth: 2 }} aria-hidden="true">
        <path d="M9 5l7 7-7 7" />
    </svg>
);
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" style={{ ...iconBase, width: 14, height: 14, strokeWidth: 2.4 }} aria-hidden="true">
        <path d="M4 12.5l5.2 5L20 6.5" />
    </svg>
);
const BlockedIcon = () => (
    <svg viewBox="0 0 24 24" style={{ ...iconBase, width: 14, height: 14, strokeWidth: 2 }} aria-hidden="true">
        <circle cx="12" cy="12" r="9" /><path d="M5.6 5.6l12.8 12.8" />
    </svg>
);
const SpinnerIcon = () => (
    <svg viewBox="0 0 24 24" style={{ ...iconBase, width: 14, height: 14, strokeWidth: 2.4 }} aria-hidden="true">
        <path d="M12 3a9 9 0 0 1 9 9">
            <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12"
                              dur="0.9s" repeatCount="indefinite" />
        </path>
    </svg>
);

const CHANNELS = {
    webpush:  { icon: BellIcon,     label: 'msg.browser_push', desc: 'msg.browser_push_desc' },
    telegram: { icon: TelegramIcon, label: 'msg.tg_push',      desc: 'msg.tg_push_desc' },
    email:    { icon: MailIcon,     label: 'msg.email_push',   desc: 'msg.email_push_desc' },
};

/**
 * 原生 SDK WebView 里根本没有「浏览器推送」这回事 —— 设备推送由 SDK 走
 * FCM/APNs 原生注册（服务端 presence 判离线自动触发），跟页面里的
 * service worker 订阅是两套完全无关的机制。服务端下发 methods 时并不知道
 * 渲染上下文（同一个 client 可能同时在网页和 App 里聊），所以只能在渲染侧
 * 按「此刻运行在哪」过滤：
 *   1. bridge transport 存在（Android/iOS/Flutter 的 JS 接口对象，页面
 *      加载起就有，不依赖 hello 握手完成）；
 *   2. 兜底：widget.html 从 URL 抄给 window 的 SDK 身份参数（fp/did）——
 *      覆盖 bridge 未注入/初始化失败的旧版 WebView。
 */
function inNativeApp() {
    if (nativeBridge.isNative()) return true;
    try {
        return !!(window.laneChatSdkFp || window.laneChatSdkDeviceId);
    } catch (e) { return false; }
}

export default class NotificationCard extends Component {
    constructor(props) {
        super(props);
        const seed = props.bindingStatus || {};
        this.state = {
            // 每个渠道一份：{ status, detail, error }
            // status ∈ idle | working | on | pending | blocked | unavailable | error
            ch: {
                webpush:  { status: seed.webpush ? 'on' : 'idle', detail: '' },
                telegram: { status: seed.telegram ? 'on' : 'idle', detail: '' },
                email:    { status: seed.email ? 'on' : 'idle', detail: seed.email ? (props.clientEmail || '') : '' },
            },
            open: null,          // 当前展开的渠道 key（内联面板）
            tgDeepLink: null,
            tgCode: null,
            emailStage: 'ask',   // ask | code
            emailValue: '',
            codeValue: '',
            notice: '',          // 卡片级瞬时反馈（"已关闭" 之类）
        };
        this._mounted = false;
        this._emailInput = null;
        this._codeInput = null;
    }

    componentDidMount() {
        this._mounted = true;
        this.syncBrowserPushState();
    }

    componentWillUnmount() { this._mounted = false; }

    setCh(key, patch) {
        if (!this._mounted) return;
        this.setState(prev => ({ ch: { ...prev.ch, [key]: { ...prev.ch[key], ...patch } } }));
    }

    /**
     * 浏览器推送的真实状态只有浏览器自己知道：服务端记着有活跃订阅，不代表
     * 「这台设备的这个浏览器」还授权着。permission + getSubscription() 复核。
     */
    async syncBrowserPushState() {
        if (this.props.methods.indexOf('webpush') === -1) return;
        if (inNativeApp()) return;   // 行都不渲染（见 render 的过滤），别再去探测浏览器能力
        const pm = this.pushManager();
        try {
            // 1. 浏览器真的没有这两个 API —— 只有这一种情况才可以说「浏览器不支持」
            const browserCan = typeof Notification !== 'undefined'
                && typeof navigator !== 'undefined'
                && 'serviceWorker' in navigator && 'PushManager' in window;
            if (!browserCan || !pm) {
                this.setCh('webpush', { status: 'unavailable', detail: 'browser' });
                return;
            }
            // 2. 当前站点有没有可用的 sw-lane.js。渲染时只用「零请求」那一档
            //    （sessionStorage 缓存 / 已有 SW 注册）—— 不能因为画一张卡片就往
            //    每个商户站点打一个可能 404 的探测请求（那会在他们控制台刷红）。
            //    还判不出来就先当可用；访客真去点的时候再打真实探测。
            const swOk = await pm.ensureSupportKnown(false);
            if (swOk === false) {
                this.setCh('webpush', { status: 'unavailable', detail: pm.unsupportedReason || 'no_sw' });
                return;
            }
            // 3. 访客此前拒绝过权限 —— 这条访客自己能解决，跟「不可用」完全两回事
            if (Notification.permission === 'denied') {
                this.setCh('webpush', { status: 'blocked' });
                return;
            }
            if (Notification.permission === 'granted') {
                const subscribed = await pm.checkSubscription();
                this.setCh('webpush', { status: subscribed ? 'on' : 'idle' });
                return;
            }
            this.setCh('webpush', { status: 'idle' });
        } catch (e) {
            // 探测失败不该把行卡死 —— 留在 idle，点了再走真实流程报真实结果
            this.setCh('webpush', { status: 'idle' });
        }
    }

    pushManager() {
        return (this.props.chat && this.props.chat.pushManager) || window.laneChatPushManager || null;
    }

    appId() {
        const c = this.props.chat;
        return (c && ((c.conf && c.conf.app_id) || (c.props && c.props.appId))) || null;
    }

    clientId() {
        const c = this.props.chat;
        return (c && c.client_id) || null;
    }

    notice(text) {
        if (!this._mounted) return;
        this.setState({ notice: text });
        clearTimeout(this._noticeTimer);
        this._noticeTimer = setTimeout(() => { if (this._mounted) this.setState({ notice: '' }); }, 4000);
    }

    // ---------------------------------------------------------------- 浏览器推送

    handlePushClick = async () => {
        const cur = this.state.ch.webpush.status;
        if (cur === 'working') return;

        if (cur === 'unavailable') { this.toggleOpen('webpush'); return; }
        if (cur === 'blocked')     { this.toggleOpen('webpush'); return; }

        if (cur === 'on') {
            // 已开启 → 点击即关闭（并给出明确反馈）
            this.setCh('webpush', { status: 'working' });
            try {
                const pm = this.pushManager();
                if (pm) await pm.unsubscribe();
                this.setCh('webpush', { status: 'idle', detail: '' });
                this.notice(t('msg.notif_turned_off'));
            } catch (e) {
                this.setCh('webpush', { status: 'on', detail: '' });
                this.notice(t('binding.push_disable_failed', { error: (e && e.message) || '' }));
            }
            return;
        }

        // idle → 申请权限并订阅
        this.setCh('webpush', { status: 'working' });
        try {
            const pm = this.pushManager();
            if (!pm) { this.setCh('webpush', { status: 'error', detail: t('binding.push_not_ready') }); return; }
            // 访客表达了意图 → 现在才允许打真实探测请求把「站点到底能不能推」定死
            const swOk = await pm.ensureSupportKnown(true);
            if (!swOk) {
                this.setCh('webpush', { status: 'unavailable', detail: pm.unsupportedReason || 'no_sw' });
                return;
            }
            await pm.subscribe();
            this.setCh('webpush', { status: 'on', detail: '' });
        } catch (e) {
            const msg = (e && e.message) || '';
            if (/permission denied/i.test(msg) || (typeof Notification !== 'undefined' && Notification.permission === 'denied')) {
                this.setCh('webpush', { status: 'blocked' });
                this.setState({ open: 'webpush' });
            } else {
                this.setCh('webpush', { status: 'error', detail: t('msg.notif_failed') });
            }
        }
    };

    // ---------------------------------------------------------------- Telegram

    handleTelegramClick = async () => {
        const cur = this.state.ch.telegram.status;
        if (cur === 'working') return;
        if (cur === 'on' || cur === 'pending' || cur === 'error') { this.toggleOpen('telegram'); return; }

        this.setCh('telegram', { status: 'working' });
        try {
            const res = await ApiService.generateTelegramBinding(this.appId(), this.clientId());
            if (res && res.success && res.deep_link) {
                this.setState({ tgDeepLink: res.deep_link, tgCode: res.code || null, open: 'telegram' });
                this.setCh('telegram', { status: 'pending', detail: '' });
                return;
            }
            // 兜底：后端没给深链，但 app 配了 bot username
            const bot = this.props.chat && this.props.chat.appConfig && this.props.chat.appConfig.telegram_bot_username;
            const appPub = this.appId();
            if (bot && appPub) {
                this.setState({ tgDeepLink: `https://t.me/${bot}?start=${appPub}`, tgCode: null, open: 'telegram' });
                this.setCh('telegram', { status: 'pending', detail: '' });
                return;
            }
            this.setCh('telegram', { status: 'error', detail: t('binding.telegram_failed') });
            this.setState({ open: 'telegram' });
        } catch (e) {
            this.setCh('telegram', { status: 'error', detail: t('binding.telegram_failed') });
            this.setState({ open: 'telegram' });
        }
    };

    openTelegram = (e) => {
        const link = this.state.tgDeepLink;
        if (!link || !/^(https:\/\/|tg:)/i.test(link)) { e.preventDefault(); return; }
        if (nativeBridge.isActive() && nativeBridge.supports('openUrl')) {
            e.preventDefault();
            openLink(link, { source: 'tg_binding' });
        }
    };

    copyTgCode = () => {
        const code = this.state.tgCode;
        if (!code || !navigator.clipboard) return;
        navigator.clipboard.writeText(`/verify ${code}`)
            .then(() => this.notice(t('binding.copied')))
            .catch(() => this.notice(t('msg.notif_failed')));
    };

    // ---------------------------------------------------------------- 邮件

    handleEmailClick = async () => {
        const cur = this.state.ch.email.status;
        if (cur === 'working') return;
        if (cur === 'on') { this.toggleOpen('email'); return; }

        const known = this.props.clientEmail;
        if (known) {
            // 「已有邮箱」这条分支此前是彻底静默的：它走 EmailBinding.showEmailBindingCard()
            // → writeToMessages({type:'card', text:''})，而 writeToMessages 对没有 text
            // 的非 binding/rating 消息直接 return —— 卡片根本没进列表，点了什么都不发生。
            // 现在原地确认，成功/失败都有可见结果。
            this.setCh('email', { status: 'working' });
            try {
                const res = await ApiService.confirmEmailBinding(this.appId(), this.clientId());
                if (res && res.success) {
                    this.setCh('email', { status: 'on', detail: res.email || known });
                    this.setState({ open: null });
                    return;
                }
                // 后端说要重新填 → 展开填写面板（而不是无声吞掉）
                this.setCh('email', { status: 'idle', detail: '' });
                this.setState({ open: 'email', emailStage: 'ask', emailValue: '' });
            } catch (e) {
                this.setCh('email', { status: 'idle', detail: '' });
                this.setState({ open: 'email', emailStage: 'ask', emailValue: '' });
                this.notice(t('email.error_send_network'));
            }
            return;
        }

        this.setState(prev => ({
            open: prev.open === 'email' ? null : 'email',
            emailStage: 'ask',
        }));
    };

    validateEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

    sendEmailCode = async () => {
        const email = (this.state.emailValue || '').trim();
        if (!email) { this.notice(t('email.error_enter_address')); return; }
        if (!this.validateEmail(email)) { this.notice(t('email.error_invalid_address')); return; }

        this.setCh('email', { status: 'working' });
        try {
            const res = await ApiService.generateBinding(
                email, this.appId(), this.clientId(),
                this.props.chat && this.props.chat.fp_visitor
            );
            if (res && res.success) {
                this.setCh('email', { status: 'idle' });
                this.setState({ emailStage: 'code', codeValue: '' });
                this.notice(t('email.code_sent_hint'));
                setTimeout(() => { if (this._codeInput) this._codeInput.focus(); }, 30);
            } else {
                this.setCh('email', { status: 'idle' });
                this.notice((res && res.message) || t('email.error_send_failed'));
            }
        } catch (e) {
            this.setCh('email', { status: 'idle' });
            this.notice(t('email.error_send_network'));
        }
    };

    verifyEmailCode = async () => {
        const code = (this.state.codeValue || '').trim();
        if (code.length !== 6) { this.notice(t('email.error_code_length')); return; }

        this.setCh('email', { status: 'working' });
        try {
            const res = await ApiService.verifyEmail(code, (this.state.emailValue || '').trim(), this.appId(), this.clientId());
            if (res && res.success) {
                this.setCh('email', { status: 'on', detail: (this.state.emailValue || '').trim() });
                this.setState({ open: null, emailStage: 'ask', codeValue: '' });
            } else {
                this.setCh('email', { status: 'idle' });
                this.notice((res && res.message) || t('email.error_verify_failed'));
            }
        } catch (e) {
            this.setCh('email', { status: 'idle' });
            this.notice(t('email.error_verify_network'));
        }
    };

    // ---------------------------------------------------------------- 渲染

    toggleOpen(key) { this.setState(prev => ({ open: prev.open === key ? null : key })); }

    handleRowEnter = (e) => { e.currentTarget.style.background = ROW_HOVER_BG; };
    handleRowLeave = (e) => { e.currentTarget.style.background = 'transparent'; };

    statusBadge(status) {
        if (status === 'on')      return { Icon: CheckIcon,   text: t('msg.notif_on'),          color: OK_COLOR,   bg: OK_TINT };
        if (status === 'working') return { Icon: SpinnerIcon, text: t('msg.notif_working'),     color: MUTED_COLOR, bg: HAIRLINE };
        if (status === 'pending') return { Icon: ClockIcon,   text: t('msg.notif_pending'),     color: WARN_COLOR, bg: WARN_TINT };
        if (status === 'blocked') return { Icon: BlockedIcon, text: t('msg.notif_blocked'),     color: WARN_COLOR, bg: WARN_TINT };
        if (status === 'unavailable') return { Icon: BlockedIcon, text: t('msg.notif_unavailable'), color: MUTED_COLOR, bg: HAIRLINE };
        if (status === 'error')   return { Icon: BlockedIcon, text: t('msg.notif_failed'),      color: WARN_COLOR, bg: WARN_TINT };
        return null;
    }

    renderRow(key, isFirst, accent, tint) {
        const meta = CHANNELS[key];
        const cur = this.state.ch[key];
        const badge = this.statusBadge(cur.status);
        const Icon = meta.icon;
        const label = t(meta.label);
        const desc = (cur.status === 'on' && cur.detail) ? cur.detail : t(meta.desc);
        const onClick = key === 'webpush' ? this.handlePushClick
                      : key === 'telegram' ? this.handleTelegramClick
                      : this.handleEmailClick;
        // 确实用不了的渠道不做成可点的按钮 —— 点了没有任何事可发生的按钮，
        // 比直接画成禁用更让人困惑。原因就写在下面常驻的说明里。
        const dead = cur.status === 'unavailable';

        return (
            <button
                key={key}
                type="button"
                data-lc-channel={key}
                disabled={dead}
                onClick={dead ? undefined : onClick}
                onMouseEnter={dead ? undefined : this.handleRowEnter}
                onMouseLeave={dead ? undefined : this.handleRowLeave}
                aria-label={`${label} — ${badge ? badge.text : desc}`}
                style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--lc-space-2)',
                    width: '100%', boxSizing: 'border-box', padding: '10px 14px',
                    background: 'transparent', border: 'none',
                    borderTop: isFirst ? 'none' : `1px solid ${HAIRLINE_BORDER}`,
                    cursor: dead ? 'default' : 'pointer', opacity: dead ? 0.72 : 1,
                    textAlign: 'left', fontFamily: 'inherit',
                    transition: 'background var(--lc-dur-fast) var(--lc-ease-out)', WebkitTapHighlightColor: 'transparent',
                }}
            >
                <span style={{
                    width: '28px', height: '28px', borderRadius: 'var(--lc-radius-sm)', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: cur.status === 'on' ? OK_TINT : tint,
                    color: cur.status === 'on' ? OK_COLOR : accent,
                }}>
                    <Icon />
                </span>
                {/* minWidth:0 是关键 —— flex 子项默认 min-width:auto，de/ru 的长复合词
                    会把整行撑破而不是换行。 */}
                <span style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <span style={{
                        display: 'block', fontSize: '13px', fontWeight: 600,
                        color: TITLE_COLOR, lineHeight: 1.35, overflowWrap: 'anywhere',
                    }}>{label}</span>
                    <span style={{
                        display: 'block', fontSize: '12px', color: MUTED_COLOR,
                        lineHeight: 1.35, marginTop: '1px', overflowWrap: 'anywhere',
                    }}>{desc}</span>
                </span>
                {badge ? (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 'var(--lc-space-1)', flexShrink: 0,
                        padding: '3px 8px', borderRadius: 'var(--lc-radius-full)', fontSize: '11px', fontWeight: 600,
                        color: badge.color, background: badge.bg, maxWidth: '46%',
                    }}>
                        <badge.Icon />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{badge.text}</span>
                    </span>
                ) : (
                    <span style={{ color: FAINT_COLOR, display: 'flex', flexShrink: 0 }}><ChevronIcon /></span>
                )}
            </button>
        );
    }

    panelStyle() {
        return {
            padding: '10px 14px 12px', borderTop: `1px solid ${HAIRLINE_BORDER}`,
            background: 'var(--lc-bg, #fbfcfd)', fontSize: '12px', color: BODY_COLOR, lineHeight: 1.5,
        };
    }

    inputStyle() {
        return {
            width: '100%', boxSizing: 'border-box', padding: '8px 10px',
            border: `1px solid ${NEUTRAL_BORDER}`, borderRadius: 'var(--lc-radius-sm)',
            fontSize: '13px', fontFamily: 'inherit', color: TITLE_COLOR,
            background: 'var(--lc-surface, #ffffff)', outline: 'none', marginTop: 'var(--lc-space-2)',
        };
    }

    actionStyle(accent) {
        return {
            width: '100%', boxSizing: 'border-box', marginTop: 'var(--lc-space-2)', padding: '9px 12px',
            border: 'none', borderRadius: 'var(--lc-radius-sm)', background: accent, color: '#ffffff',
            fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
        };
    }

    ghostStyle(accent) {
        return {
            width: '100%', boxSizing: 'border-box', marginTop: '6px', padding: '7px 12px',
            border: 'none', borderRadius: 'var(--lc-radius-sm)', background: 'transparent', color: accent,
            fontSize: '12px', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
        };
    }

    renderPanel(key, accent) {
        const cur = this.state.ch[key];
        // 「用不了」的说明常驻：这一行本来就点不动（渲染成 disabled），
        // 说明要是还得点一下才看得见，访客只会以为按钮坏了。
        const alwaysOn = cur.status === 'unavailable';
        if (!alwaysOn && this.state.open !== key) return null;

        if (key === 'webpush') {
            if (cur.status === 'unavailable') {
                // 三种「用不了」说三句不同的话，不塌缩成一句甩锅给浏览器的
                const others = this.props.methods.filter(m => m !== 'webpush');
                return (
                    <div style={this.panelStyle()}>
                        {cur.detail === 'browser'
                            ? t('msg.notif_push_no_browser')
                            : t('msg.notif_push_no_site')}
                        {others.length > 0 && (
                            <div style={{ marginTop: '6px', color: MUTED_COLOR }}>
                                {t('msg.notif_push_use_other', {
                                    channels: others.map(m => t(CHANNELS[m].label)).join(' · '),
                                })}
                            </div>
                        )}
                    </div>
                );
            }
            return <div style={this.panelStyle()}>{t('msg.notif_blocked_help')}</div>;
        }

        if (key === 'telegram') {
            if (cur.status === 'on') {
                return <div style={this.panelStyle()}>{t('msg.notif_tg_on_detail')}</div>;
            }
            if (cur.status === 'error') {
                return <div style={this.panelStyle()}>{cur.detail || t('binding.telegram_failed')}</div>;
            }
            const link = this.state.tgDeepLink;
            return (
                <div style={this.panelStyle()}>
                    <div>{t('msg.notif_tg_pending_hint')}</div>
                    {link && (
                        <a href={link} target="_blank" rel="noopener noreferrer"
                           onClick={this.openTelegram}
                           style={{ ...this.actionStyle(accent), display: 'block', textAlign: 'center', textDecoration: 'none' }}>
                            {t('binding.open_telegram')}
                        </a>
                    )}
                    {this.state.tgCode && (
                        <button type="button" style={this.ghostStyle(accent)} onClick={this.copyTgCode}>
                            {t('binding.copy_command')}
                        </button>
                    )}
                </div>
            );
        }

        // email
        if (cur.status === 'on') {
            return <div style={this.panelStyle()}>{t('msg.notif_email_on_detail', { email: cur.detail || '' })}</div>;
        }
        const busy = cur.status === 'working';
        if (this.state.emailStage === 'code') {
            return (
                <div style={this.panelStyle()}>
                    <div>{t('email.code_sent_hint')}</div>
                    <input
                        type="text" inputMode="numeric" maxLength={6}
                        ref={(el) => { this._codeInput = el; }}
                        style={this.inputStyle()}
                        placeholder={t('email.input_code')}
                        value={this.state.codeValue}
                        onChange={(e) => this.setState({ codeValue: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') this.verifyEmailCode(); }}
                    />
                    <button type="button" style={this.actionStyle(accent)} disabled={busy} onClick={this.verifyEmailCode}>
                        {busy ? t('email.verifying') : t('email.verify_cta')}
                    </button>
                    <button type="button" style={this.ghostStyle(accent)}
                            onClick={() => this.setState({ emailStage: 'ask', codeValue: '' })}>
                        {t('email.use_other')}
                    </button>
                </div>
            );
        }
        return (
            <div style={this.panelStyle()}>
                <input
                    type="email"
                    ref={(el) => { this._emailInput = el; }}
                    style={this.inputStyle()}
                    placeholder={t('email.input_email')}
                    value={this.state.emailValue}
                    onChange={(e) => this.setState({ emailValue: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') this.sendEmailCode(); }}
                />
                <button type="button" style={this.actionStyle(accent)} disabled={busy} onClick={this.sendEmailCode}>
                    {busy ? t('email.sending') : t('email.send_code')}
                </button>
            </div>
        );
    }

    render() {
        const { methods = [], allowMultiple = true, isOffline = false, text, theme } = this.props;
        const accent = (theme && theme.mainColor) || '#2563eb';
        const tint = tintOf(accent);
        const keys = ['webpush', 'telegram', 'email']
            .filter(k => methods.indexOf(k) !== -1)
            // 原生 App 里没有浏览器推送 —— 设备推送 SDK 已经原生接管，这一行
            // 只会让访客困惑（开了也不是他以为的那个东西）。telegram/email 照常。
            .filter(k => !(k === 'webpush' && inNativeApp()));

        const HeadIcon = isOffline ? ClockIcon : BellIcon;
        const title = isOffline ? t('msg.notifications_title') : t('msg.notifications_cta');
        const body = text || (allowMultiple ? t('msg.notifications_pick_multi') : t('msg.notifications_pick_single'));

        return (
            <div style={{
                background: 'var(--lc-surface, #ffffff)', border: 'var(--lc-hairline)', borderRadius: 'var(--lc-radius-md)',
                overflow: 'hidden', maxWidth: '320px', boxShadow: 'var(--lc-shadow-1)',
            }}>
                <div style={{ padding: '12px 14px' }}>
                    {/* flex-start + 2px 上边距：de / ru 的长标题会折两行，居中会让图标
                        浮在两行之间；贴第一行的视觉中线才对。 */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--lc-space-2)' }}>
                        <span style={{ color: accent, display: 'flex', flexShrink: 0, marginTop: '2px' }}>
                            <HeadIcon />
                        </span>
                        <h4 style={{
                            margin: 0, fontSize: '15px', fontWeight: 600, color: TITLE_COLOR,
                            lineHeight: 1.35, minWidth: 0, overflowWrap: 'anywhere',
                        }}>{title}</h4>
                    </div>
                    {body && (
                        <p style={{
                            margin: '6px 0 0 0', fontSize: '13px', lineHeight: 1.45,
                            color: BODY_COLOR, overflowWrap: 'anywhere',
                        }}>{body}</p>
                    )}
                </div>

                {keys.length > 0 && (
                    <div style={{ borderTop: `1px solid ${HAIRLINE_BORDER}`, display: 'flex', flexDirection: 'column' }}>
                        {keys.map((k, i) => (
                            <React.Fragment key={k}>
                                {this.renderRow(k, i === 0, accent, tint)}
                                {this.renderPanel(k, accent)}
                            </React.Fragment>
                        ))}
                    </div>
                )}

                {this.state.notice && (
                    <div data-lc-notice="1" style={{
                        borderTop: `1px solid ${HAIRLINE_BORDER}`, padding: '8px 14px',
                        fontSize: '12px', color: BODY_COLOR, lineHeight: 1.4, background: 'var(--lc-bg, #fbfcfd)',
                    }}>{this.state.notice}</div>
                )}

                {allowMultiple && keys.length > 1 && (
                    <div style={{
                        borderTop: `1px solid ${HAIRLINE_BORDER}`, padding: '8px 14px',
                        fontSize: '12px', color: FAINT_COLOR, lineHeight: 1.4,
                    }}>{t('msg.notifications_multi_hint')}</div>
                )}
            </div>
        );
    }
}
